import {
  Autocomplete,
  Box,
  Button,
  DialogActions,
  DialogContent,
  Link,
  TextField,
  Typography,
} from "@mui/material"
import Grid from "@mui/material/Grid"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import {
  DataGridPro,
  GridColDef,
  GridRowSelectionModel,
} from "@mui/x-data-grid-pro"
import { dataGridDEde } from "constants/dataGridLocale"
import {
  RoundOrderArticle,
  SplitableStatus,
  SupplierOrderItem,
} from "generated/graphql"
import { fetchSplitableMergeable } from "queries/getSplitableMergeableStatuslist"
import { useEffect, useMemo, useState } from "react"

export interface RoundOrderSplitItemDialogProps {
  open: boolean
  onClose: () => void
  supplierName: string
  selection: RoundOrderArticle[]
  supplierOrderInfo: SupplierOrderItem[]
  onSplit: (
    splitItems: SupplierOrderItem[],
    selectedStatus: number,
    statusCodesToSumUp: number[]
  ) => void
  isLoading: boolean
}

export const RoundOrderSplitItemDialog: React.FC<
  RoundOrderSplitItemDialogProps
> = (props) => {
  const {
    open,
    onClose,
    selection,
    supplierOrderInfo,
    supplierName,
    onSplit,
    isLoading,
  } = props

  const [selectedSplitItems, setSelectedSplitItems] =
    useState<GridRowSelectionModel>({ type: "include", ids: new Set() })
  const [selectedStatus, setSelectedStatus] = useState<number>(0)
  const [statusCodes, setStatusCodes] = useState<SplitableStatus[]>([])
  const [allSelectedStatuses, setAllSelectedStatuses] = useState<any>([])
  const selectedValues = useMemo(
    () => allSelectedStatuses.filter((v: any) => v.isSelected),
    [allSelectedStatuses]
  )

  const cols: GridColDef<SupplierOrderItem>[] = [
    { field: "id", headerName: "Lieferant Bestellnr.", width: 150 },
    {
      field: "orderID",
      headerName: "Order ID",
      renderCell: (params) => {
        return (
          <Link href={`/orderDetails/${params.row.orderID}`} target="_blank">
            <Typography variant="body2" color="secondary" component="div">
              {params.row.orderID}
            </Typography>
          </Link>
        )
      },
      width: 150,
    },
    { field: "ewID", headerName: "EwID", width: 100 },
    { field: "bdb", headerName: "BDB", width: 120 },
    {
      field: "botname",
      headerName: "Artikel",
      renderCell: (params) => {
        return (
          <Box>
            <Typography variant="body1" component="div">
              {params.row.botname}
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div">
              {params.row.sorte} - {params.row.size}
            </Typography>
          </Box>
        )
      },
      width: 300,
    },
    { field: "quantity", headerName: "Anzahl", width: 100 },
  ]

  const filterSelctedData = (
    data: SupplierOrderItem[]
  ): SupplierOrderItem[] => {
    const l: SupplierOrderItem[] = []
    for (let i = 0; i < selection.length; i++) {
      for (let j = 0; j < data.length; j++) {
        if (selection[i].articleId === data[j].bdb) {
          l.push(data[j])
        }
      }
    }
    return l
  }

  const getSelectedItems = () => {
    const selectedItems: SupplierOrderItem[] = []
    for (let i = 0; i < supplierOrderInfo.length; i++) {
      if (selectedSplitItems.ids.has(supplierOrderInfo[i].id)) {
        selectedItems.push(supplierOrderInfo[i])
      }
    }
    return selectedItems
  }

  const handleSplitItems = () => {
    const splitItems = getSelectedItems()
    onSplit(
      splitItems,
      selectedStatus,
      selectedValues.map((v: any) => v.status)
    )
    // Reset the selected items
    setSelectedSplitItems({ type: "include", ids: new Set() })
    setSelectedStatus(0)
    setAllSelectedStatuses(
      statusCodes.map((v) => {
        return { ...v, isSelected: false }
      })
    )
  }

  useEffect(() => {
    fetchSplitableMergeable().then((response) => {
      if (response?.getSplitMergeStatuses) {
        setStatusCodes(response.getSplitMergeStatuses)
        setAllSelectedStatuses(
          response.getSplitMergeStatuses.map((v) => {
            return { ...v, isSelected: false }
          })
        )
      }
    })
  }, [setStatusCodes, setAllSelectedStatuses])

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle>Artikel-Splitten von {supplierName}</DialogTitle>
      <DialogContent sx={{ height: "70vh", width: "100%" }}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Box sx={{ height: "60vh", width: "100%" }}>
              <DataGridPro
                rows={filterSelctedData(supplierOrderInfo)}
                columns={cols}
                checkboxSelection
                getRowId={(row) => row.id}
                onRowSelectionModelChange={(newSelection) =>
                  setSelectedSplitItems(newSelection)
                }
                disableRowSelectionOnClick
                localeText={dataGridDEde}
                loading={isLoading}
              />
            </Box>
          </Grid>
          <Grid size={6}>
            <Autocomplete
              disablePortal
              id="nextStatusCode"
              getOptionLabel={(option) => option.status + " - " + option.remark}
              fullWidth
              isOptionEqualToValue={(option, value) =>
                option.status === value.status
              }
              value={
                statusCodes.find((x) => x.status === selectedStatus) ?? null
              }
              onChange={(_, newValue) => {
                if (newValue) setSelectedStatus(newValue.status)
              }}
              options={statusCodes.filter((v) => v.splitable)}
              renderInput={(params) => <TextField {...params} label="Status" />}
            />
          </Grid>
          <Grid size={6}>
            <Autocomplete
              disablePortal
              id="multiple-status"
              getOptionLabel={(option) => option.status + " - " + option.remark}
              fullWidth
              isOptionEqualToValue={(option, value) =>
                option.status === value.status
              }
              multiple
              value={selectedValues}
              onChange={(_, newValue) => {
                setAllSelectedStatuses(
                  statusCodes.map((v) => {
                    return {
                      ...v,
                      isSelected: newValue.some((x) => x.status === v.status),
                    }
                  })
                )
              }}
              options={statusCodes.filter((v) => v.mergeable)}
              renderInput={(params) => (
                <TextField {...params} label="Weitere Status Codes beachten" />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setSelectedSplitItems({ type: "include", ids: new Set() })
            onClose()
          }}
          fullWidth
          variant="contained"
          color="secondary"
        >
          Abbrechen
        </Button>
        <Button
          variant="contained"
          loading={isLoading}
          fullWidth
          onClick={handleSplitItems}
          disabled={selectedSplitItems.ids.size === 0 || selectedStatus === 0}
          color="primary"
        >
          Splitten
        </Button>
      </DialogActions>
    </Dialog>
  )
}
